import { useQuery } from '@tanstack/react-query';
import { Loader2, ExternalLink } from 'lucide-react';
import { useMemo, useState, useCallback, ReactNode } from 'react';

import { Link, Button } from '@/components/design-system';
import {
  COLOR_SCALE,
  GAP_SCALE,
  PADDING_Y,
  SIZE_SCALE,
  TYPE_SCALE,
} from '@/components/design-system/tokens';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { supabase } from '@/integrations/supabase/client';
import { createPageUrl } from '@/utils';

import { GlossaryContext, GlossaryTerm } from './GlossaryContext';

// Provider props interface
interface GlossaryProviderProps {
  children: ReactNode;
}

const computeLetter = (term: GlossaryTerm): string => {
  const firstChar = term.term.trim().charAt(0).toUpperCase() || '#';
  return /^[A-Z]$/.test(firstChar) ? firstChar : '#';
};

const normalizeTerms = (terms: Partial<GlossaryTerm>[]): GlossaryTerm[] =>
  terms
    .filter((term): term is Partial<GlossaryTerm> & { id: string; term: string } =>
      Boolean(term.id && term.term)
    )
    .map(term => {
      const letter = computeLetter(term as GlossaryTerm);
      const normalizedTerm: GlossaryTerm = {
        id: term.id,
        term: term.term,
        description: term.description ?? null,
        letter,
      };
      return normalizedTerm;
    });

export function GlossaryProvider({ children }: GlossaryProviderProps) {
  const [termsOverride] = useState(null);
  // React Query to load glossary terms
  const { data: fetchedTerms = [], isLoading } = useQuery({
    queryKey: ['glossary', 'terms'],
    staleTime: 1000 * 60 * 60,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('glossary')
        .select('id, term, description, letter')
        .eq('archived', false)
        .order('term');
      if (error) throw error;
      return normalizeTerms(data);
    },
  });

  const terms = fetchedTerms;
  const loading = isLoading && !termsOverride;
  const [activeTermId, setActiveTermId] = useState<string | null>(null);

  const termIndex = useMemo(() => {
    const map = new Map<string, GlossaryTerm>();
    for (const term of terms) {
      if (term.term) {
        map.set(term.term.toLowerCase(), term);
      }
    }
    return map;
  }, [terms]);

  const getTermById = useMemo(() => {
    const map = new Map<string, GlossaryTerm>();
    for (const term of terms) {
      if (term.id) {
        map.set(term.id, term);
      }
    }
    return map;
  }, [terms]);

  const openTermByName = useCallback(
    (name: string) => {
      if (!name) return;
      const entry = termIndex.get(name.trim().toLowerCase());
      if (entry?.id) {
        setActiveTermId(entry.id);
      }
    },
    [termIndex]
  );

  const openTermById = useCallback((id: string) => {
    if (id) {
      setActiveTermId(id);
    }
  }, []);

  const closeTerm = useCallback(() => {
    setActiveTermId(null);
  }, []);

  const activeTerm = useMemo(() => {
    if (!activeTermId) return null;
    return getTermById.get(activeTermId) ?? null;
  }, [activeTermId, getTermById]);

  const contextValue = useMemo(
    () => ({
      terms,
      loading,
      termIndex,
      openTermByName,
      openTermById,
      closeTerm,
      activeTerm,
    }),
    [terms, loading, termIndex, activeTerm, openTermByName, openTermById, closeTerm]
  );

  return (
    <GlossaryContext.Provider value={contextValue}>
      {children}
      <Dialog open={Boolean(activeTerm)} onOpenChange={open => !open && closeTerm()}>
        <DialogContent>
          {activeTerm ? (
            <>
              <DialogHeader>
                <DialogTitle>{activeTerm.term}</DialogTitle>
                {activeTerm.description ? (
                  <DialogDescription
                    className={`${TYPE_SCALE.base} leading-relaxed ${COLOR_SCALE['foreground-subtle']}`}
                  >
                    {activeTerm.description}
                  </DialogDescription>
                ) : (
                  <DialogDescription
                    className={`${TYPE_SCALE.sm} ${COLOR_SCALE['foreground-muted']}`}
                  >
                    No description has been added yet.
                  </DialogDescription>
                )}
              </DialogHeader>
              <div className="flex items-center justify-end">
                <Button asChild variant="outline" size="sm">
                  <Link
                    to={`${createPageUrl('Glossary')}?term=${encodeURIComponent(activeTerm.term)}`}
                    className={`inline-flex items-center ${GAP_SCALE['2']}`}
                    onClick={closeTerm}
                  >
                    <ExternalLink className={`${SIZE_SCALE.h4} ${SIZE_SCALE.w4}`} />
                    View full glossary
                  </Link>
                </Button>
              </div>
            </>
          ) : (
            <div className={`flex items-center justify-center ${PADDING_Y['6']}`}>
              <Loader2
                className={`${SIZE_SCALE['6']} ${SIZE_SCALE.w6} animate-spin text-primary`}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </GlossaryContext.Provider>
  );
}
